import { toTitleCase } from 'Helpers/StringUtils';
import { FileToBeUploaded, FileUpdates, UploadedFile } from 'Models/Files';
import { OperationalControl } from 'Models/OperationalControls';
import { GroupOptionType, OptionType } from 'Models/Types/GlobalType';

import { DueDiligenceModule, ProductModule } from './ProductModule';
import { DisplayableTag } from './Tags';

/**
 * Identifies the page a user navigated from when clicking "Create Exception" in `ExceptionsListing`.
 * Used to render an origin-aware breadcrumb on the Manage Exception page, so the user can navigate back to where they came from rather than seeing the default "Exceptions > Manage Exception" breadcrumb.
 */
export enum ExceptionBreadcrumbSource {
    ASSESSMENT = 'assessment',
    CONTROL_DETAILS = 'controlDetails',
    EXCEPTION_DETAILS = 'exceptionDetails',
    EXCEPTIONS_LISTING = 'exceptionsListing',
    FINAL_REVIEW = 'finalReview',
    ASSESSMENT_SUBJECT_DASHBOARD = 'assessmentSubjectDashboard',
}

/**
 * The context needed to reconstruct the breadcrumb trail of the page a "Create Exception" action originated from.
 * The IDs and human-readable display names are carried in the create-exception URL's search params (see `getCreateExceptionUrl`).
 */
export type ManageExceptionOrigin =
    | {
          source: ExceptionBreadcrumbSource.ASSESSMENT;
          assessmentId: string;
          assessmentName: string;
          module: DueDiligenceModule;
          assessmentSubjectDisplayName: string;
          assessmentSubjectId: string;
          organizationId: string;
      }
    | {
          source: ExceptionBreadcrumbSource.CONTROL_DETAILS;
          controlDisplayName: string;
          controlGroupName: string;
          controlIdentifier: string;
      }
    | {
          source: ExceptionBreadcrumbSource.FINAL_REVIEW;
          module: DueDiligenceModule;
          assessmentSubjectDisplayName: string;
          assessmentSubjectId: string;
          organizationId: string;
      }
    | {
          source: ExceptionBreadcrumbSource.EXCEPTION_DETAILS;
          module: ProductModule;
      }
    | {
          source: ExceptionBreadcrumbSource.EXCEPTIONS_LISTING;
          module: ProductModule;
      }
    | {
          source: ExceptionBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD;
          module: DueDiligenceModule;
          assessmentSubjectDisplayName: string;
          assessmentSubjectId: string;
          organizationId: string;
      };

/**
 * This represents an Exception. The "-Entity" suffix is used to avoid shadowing Python's built-in `Exception` type.
 * Create and update requests write this to the database.
 * This is not sent in responses. Instead, it is converted to an ExceptionResponse DTO, since the frontend needs to know:
 * 1. Whether the Exception belongs to the Operational Controls module or the TPRM module.
 * 2. Additional details about the controls that an Exception is mapped to, if it belongs in the Operational Controls module.
 */
export interface ExceptionEntity {
    id: string;
    created_by: string;
    created_timestamp: string;
    description: string;
    last_updated_by: string;
    last_updated_timestamp: string;
    status: ExceptionStatus;
    title: string;
    type: ProductModule;

    compensating_controls?: string;
    delegates: string[];
    expiration_date?: string;
    files: UploadedFile[];
    impact?: ExceptionImpact;
    impacted_controls: string[];
    impacted_vendor?: string;
    likelihood?: ExceptionLikelihood;
    owner_subject?: string;
    reference?: string;
    remediation_plan?: string;
    reviewer_subject?: string;
    risk_assessment?: string;
    risk_score?: ExceptionRiskScore; // Calculated based on impact and likelihood.
    tags: string[];

    // These fields are always present when the issue is CLOSED.
    closure_statement?: string;
    closed_timestamp?: string;
    closed_by?: string;
}

/** A request to create a new Exception. */
export interface CreateExceptionRequest {
    title: string;
    description: string;
    type: ProductModule;
    expiration_date?: string;
    likelihood?: ExceptionLikelihood;
    impact?: ExceptionImpact;
    remediation_plan?: string;
    reviewer_subject?: string;
    owner_subject?: string;
    risk_assessment?: string;

    delegates: string[];
    reference?: string;
    compensating_controls?: string;
    impacted_controls: string[];
    impacted_vendor?: string;
    files: FileToBeUploaded[];
    tags: string[];
}

/** A request to update an existing Exception. */
export interface UpdateExceptionRequest {
    title: string;
    status: ExceptionStatus;
    description: string;
    type: ProductModule;
    expiration_date?: string;
    likelihood?: ExceptionLikelihood;
    impact?: ExceptionImpact;
    remediation_plan?: string;
    reviewer_subject?: string;
    owner_subject?: string;
    risk_assessment?: string;

    delegates: string[];
    reference?: string;
    impacted_controls: string[];
    impacted_vendor?: string;
    compensating_controls?: string;
    file_updates: FileUpdates;
    tags: string[];

    closure_statement?: string;
}

/** A DTO used to make Exception presentable to front-end clients. `impacted_controls` is always enriched with full Control details rather than just Control IDs (empty when the Exception isn't mapped to Controls). */
export type ExceptionResponse = Omit<ExceptionEntity, 'impacted_controls'> & { impacted_controls: OperationalControl[] };

/** This object defines changes to an ExceptionEntity's attributes that are necessary when the ExceptionEntity is saved as a history record. */
export type ExceptionHistoryModifications = Omit<ExceptionEntity, 'tags'> & { tags: DisplayableTag[] };

/** A DTO used to make an ExceptionHistoryModifications presentable to front-end clients. See `ExceptionResponse` for the rationale behind the `impacted_controls` override. */
export type ExceptionHistoryModificationsResponse = Omit<ExceptionHistoryModifications, 'impacted_controls'> & { impacted_controls: OperationalControl[] };

/** A DTO used to make ExceptionHistory presentable to front-end clients. */
export interface ExceptionHistoryResponse {
    timestamp: string;
    exception: ExceptionHistoryModificationsResponse;
}

/**
 * A DTO used to make an ExceptionEntity presentable to front-end clients for the Export feature.
 * The difference is the Export version of an Issue needs UI-friendly versions of Controls or Third-Party names, depending on which feature the Issue belongs to.
 */
export interface ExceptionExportResponse extends Omit<ExceptionEntity, 'impacted_controls' | 'impacted_vendor'> {
    impacted_controls: OperationalControl[];
    impacted_vendor?: string; // Technically this is the same data type as defined in the ExceptionEntity interface, but it's listed here in order to highlight that this is the Third-Party's name instead of their UUID.
}

export enum ExceptionsSortFilterOptions {
    TITLE = 'title',
    EXPIRATION = 'expiration_date',
    DESCRIPTION = 'description',
    OWNER = 'owner_subject',
    REVIEWER = 'reviewer_subject',
    STATUS = 'status',
    RISK_SCORE = 'risk_score',
    CONTROLS = 'controls',
    THIRD_PARTY = 'impacted_vendor',
    TAGS = 'tags',
}

export enum ExceptionStatus {
    DRAFT_OPEN = 'DRAFT_OPEN',
    APPROVED = 'APPROVED',
    DRAFT_CLOSE = 'DRAFT_CLOSE',
    CLOSED = 'CLOSED',
}

const statusAsNumber = (status: ExceptionStatus): number => {
    switch (status) {
        case ExceptionStatus.DRAFT_OPEN:
            return 0;
        case ExceptionStatus.APPROVED:
            return 1;
        case ExceptionStatus.DRAFT_CLOSE:
            return 2;
        case ExceptionStatus.CLOSED:
            return 3;
    }
};

export const statusComparator = (statusA: ExceptionStatus, statusB: ExceptionStatus): number => {
    if (statusA === statusB) {
        return 0;
    } else {
        const valueA = statusAsNumber(statusA);
        const valueB = statusAsNumber(statusB);
        return valueA < valueB ? -1 : 1;
    }
};

export const ExceptionStatusFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: ExceptionsSortFilterOptions.STATUS,
        label: 'Draft Open',
        value: ExceptionStatus.DRAFT_OPEN,
    },
    {
        groupId: ExceptionsSortFilterOptions.STATUS,
        label: 'Approved',
        value: ExceptionStatus.APPROVED,
    },
    {
        groupId: ExceptionsSortFilterOptions.STATUS,
        label: 'Draft Close',
        value: ExceptionStatus.DRAFT_CLOSE,
    },
    {
        groupId: ExceptionsSortFilterOptions.STATUS,
        label: 'Closed',
        value: ExceptionStatus.CLOSED,
    },
];

export enum ExceptionLikelihood {
    RARE = 'RARE',
    UNLIKELY = 'UNLIKELY',
    POSSIBLE = 'POSSIBLE',
    LIKELY = 'LIKELY',
    ALMOST_CERTAIN = 'ALMOST_CERTAIN',
}

export const ExceptionLikelihoodOptions: OptionType[] = [
    {
        value: ExceptionLikelihood.RARE,
        label: toTitleCase(ExceptionLikelihood.RARE),
    },
    {
        value: ExceptionLikelihood.UNLIKELY,
        label: toTitleCase(ExceptionLikelihood.UNLIKELY),
    },
    {
        value: ExceptionLikelihood.POSSIBLE,
        label: toTitleCase(ExceptionLikelihood.POSSIBLE),
    },
    {
        value: ExceptionLikelihood.LIKELY,
        label: toTitleCase(ExceptionLikelihood.LIKELY),
    },
    {
        value: ExceptionLikelihood.ALMOST_CERTAIN,
        label: toTitleCase(ExceptionLikelihood.ALMOST_CERTAIN),
    },
];

export enum ExceptionImpact {
    LOW = 'LOW',
    LOW_MODERATE = 'LOW_MODERATE',
    MODERATE = 'MODERATE',
    MODERATE_HIGH = 'MODERATE_HIGH',
    HIGH = 'HIGH',
}

export const ExceptionImpactOptions: OptionType[] = [
    {
        value: ExceptionImpact.LOW,
        label: toTitleCase(ExceptionImpact.LOW, '/'),
    },
    {
        value: ExceptionImpact.LOW_MODERATE,
        label: toTitleCase(ExceptionImpact.LOW_MODERATE, '/'),
    },
    {
        value: ExceptionImpact.MODERATE,
        label: toTitleCase(ExceptionImpact.MODERATE, '/'),
    },
    {
        value: ExceptionImpact.MODERATE_HIGH,
        label: toTitleCase(ExceptionImpact.MODERATE_HIGH, '/'),
    },
    {
        value: ExceptionImpact.HIGH,
        label: toTitleCase(ExceptionImpact.HIGH, '/'),
    },
];

export enum ExceptionRiskScore {
    LOW = 'LOW',
    LOW_MODERATE = 'LOW_MODERATE',
    MODERATE = 'MODERATE',
    MODERATE_HIGH = 'MODERATE_HIGH',
    HIGH = 'HIGH',
}

export const ExceptionRiskScoreFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: ExceptionsSortFilterOptions.RISK_SCORE,
        label: 'Low',
        value: ExceptionRiskScore.LOW,
    },
    {
        groupId: ExceptionsSortFilterOptions.RISK_SCORE,
        label: 'Low/Moderate',
        value: ExceptionRiskScore.LOW_MODERATE,
    },
    {
        groupId: ExceptionsSortFilterOptions.RISK_SCORE,
        label: 'Moderate',
        value: ExceptionRiskScore.MODERATE,
    },
    {
        groupId: ExceptionsSortFilterOptions.RISK_SCORE,
        label: 'Moderate/High',
        value: ExceptionRiskScore.MODERATE_HIGH,
    },
    {
        groupId: ExceptionsSortFilterOptions.RISK_SCORE,
        label: 'High',
        value: ExceptionRiskScore.HIGH,
    },
];

const riskScoreAsNumber = (score: ExceptionRiskScore): number => {
    switch (score) {
        case ExceptionRiskScore.LOW:
            return 0;
        case ExceptionRiskScore.LOW_MODERATE:
            return 1;
        case ExceptionRiskScore.MODERATE:
            return 2;
        case ExceptionRiskScore.MODERATE_HIGH:
            return 3;
        case ExceptionRiskScore.HIGH:
            return 4;
    }
};

export const riskScoreComparator = (scoreA: ExceptionRiskScore, scoreB: ExceptionRiskScore): number => {
    if (scoreA === scoreB) {
        return 0;
    } else {
        const valueA = riskScoreAsNumber(scoreA);
        const valueB = riskScoreAsNumber(scoreB);
        return valueA < valueB ? 1 : -1;
    }
};

export enum ExceptionRiskScoreFilter {
    ALL = 'ALL',
    LOW = 'LOW',
    LOW_MODERATE = 'LOW_MODERATE',
    MODERATE = 'MODERATE',
    MODERATE_HIGH = 'MODERATE_HIGH',
    HIGH = 'HIGH',
}

export const isValidExceptionRiskScoreFilter = (value: unknown): value is ExceptionRiskScoreFilter => typeof value === 'string' && Object.values(ExceptionRiskScoreFilter).includes(value as ExceptionRiskScoreFilter);

export const ExceptionRiskScoreFilterOptions: OptionType[] = [
    {
        value: ExceptionRiskScoreFilter.ALL,
        label: 'All Risk Scores',
    },
    {
        value: ExceptionRiskScoreFilter.LOW,
        label: toTitleCase(ExceptionRiskScoreFilter.LOW, '/'),
    },
    {
        value: ExceptionRiskScoreFilter.LOW_MODERATE,
        label: toTitleCase(ExceptionRiskScoreFilter.LOW_MODERATE, '/'),
    },
    {
        value: ExceptionRiskScoreFilter.MODERATE,
        label: toTitleCase(ExceptionRiskScoreFilter.MODERATE, '/'),
    },
    {
        value: ExceptionRiskScoreFilter.MODERATE_HIGH,
        label: toTitleCase(ExceptionRiskScoreFilter.MODERATE_HIGH, '/'),
    },
    {
        value: ExceptionRiskScoreFilter.HIGH,
        label: toTitleCase(ExceptionRiskScoreFilter.HIGH, '/'),
    },
];

/**
 * This relatively simple function exists because we have previously had inconsistencies in whether drafts are considered "open".
 * @returns The number of issues in the provided list that are considered "open".
 */
export const countOpenExceptions = (exceptions: ExceptionResponse[]): number => exceptions.filter((exception) => exception.status === ExceptionStatus.APPROVED || exception.status === ExceptionStatus.DRAFT_CLOSE).length;
